import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DocumentDto } from '../../beneficiaries/beneficiary-details/documents/document.dto';
import { BeneficiaryDto } from '../../beneficiaries/dto/beneficiary.dto';
import { DateService } from '../services/date.service';
import { DocumentsService } from '../services/documents.service';
import { FileService } from '../services/file.service';

@Component({
  selector: 'app-file-input',
  templateUrl: './file-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: FileInputComponent,
      multi: true,
    },
  ],
})
export class FileInputComponent implements ControlValueAccessor {
  @Input() public beneficiary?: BeneficiaryDto;
  @Input() public title?: string;
  @Input() public titleId?: number;
  @Input() public accept?: string;
  @Input() public invalid: boolean;
  @Input() public maxFileSize: number = 1000 * 1000 * 1.2;
  @Input() public renameWithBeneficiaryData: boolean = true;
  @Input() public disabled: boolean = false;

  public document: DocumentDto;
  public name: string;

  public file?: File;

  constructor(
    private readonly documentsService: DocumentsService,
    private readonly fileService: FileService,
    private readonly dateService: DateService,
  ) {}

  public onChange = (value: DocumentDto): void => {};
  public onTouched = (value: DocumentDto): void => {};

  public get value(): DocumentDto {
    return this.document;
  }

  public writeValue(value: DocumentDto): void {
    this.document = value;
    this.name = value?.fileName;
    this.file = undefined;
  }

  public async onFileChange(event: Event): Promise<void> {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = input.files;
    if (files[0]) {
      this.file = files[0];
      const extension: string = this.file.name.split('.').pop();
      const fileData = await this.readFile(files[0]);
      this.name = this.renameWithBeneficiaryData
        ? `${this.beneficiary?.insertisId}_${this.title}_${this.dateService.getCurrent().getTime()}.${extension}`
        : this.file.name;
      this.document = {
        beneficiaryId: this.beneficiary?.id,
        fileName: this.name,
        fileData,
        titleId: +this.titleId,
      } as DocumentDto;
      this.onChange(this.document);
    }
  }

  public consultFile(): Promise<void> {
    if (this.document.id) {
      return this.documentsService.downloadDocument(this.document.id, this.name);
    } else if (this.name) {
      return this.fileService.saveAs(this.file, this.name);
    }
  }

  public validate() {
    const vObj: any = {};
    if (this.file?.size > this.maxFileSize) {
      vObj['fileSize'] = true;
    }
    if (this.accept && this.file && !this.accept.split(',').includes(this.file.type)) {
      vObj['fileType'] = true;
    }
    return vObj;
  }

  public registerOnChange(fn: (value: DocumentDto) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private async readFile(file: File): Promise<string> {
    if (file) {
      return new Promise<string>((resolve: (value: string) => void): void => {
        const reader: FileReader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (): void => {
          resolve(reader.result as string);
        };
      });
    }
  }
}
